import React, { useContext, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { Modal, Image, Button, Pagination, message, Spin, Popconfirm, Input } from "antd";
import { DeleteFilled, UploadOutlined, FolderAddFilled, FolderFilled, ArrowLeftOutlined } from "@ant-design/icons";

import api from "../../services/Api";
import Dragger from "antd/es/upload/Dragger";

const Gallery = () => {
    const { user, isAuthenticated, hasRole } = useContext(AuthContext);
    const { folderId } = useParams();
    const navigate = useNavigate();
    const [images, setImages] = useState([]);
    const [folders, setFolders] = useState([]);
    const [loadingImages, setLoadingImages] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(30); // Number of images per page
    const [totalImages, setTotalImages] = useState(0);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [folderModalOpen, setFolderModalOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState("");
    const [isUsersFolder, setIsUsersFolder] = useState(false);
    const [folder, setFolder] = useState({});
    const [breadcrumbs, setBreadcrumbs] = useState([]);

    if (folderId === undefined || folderId === null) {
        navigate("/oma-sivu/kuvapankki/0");
    }

    useEffect(() => {
        if (isAuthenticated) {
            fetchFolder(folderId, currentPage, pageSize);
        }
    }, [isAuthenticated, folderId, currentPage, pageSize]);

    const fetchFolder = async (folderId, page, pageSize) => {
        setLoadingImages(true);

        try {
            const response = await api.getFolder(folderId, page, pageSize);
            setFolder(response.folder);
            setImages(response.images);
            setFolders(response.folders);
            setTotalImages(response.totalFiles);
            setIsUsersFolder(response.isUsersFolder);
            setBreadcrumbs(response.breadcrumbs);
        } catch (error) {
            console.error("Error fetching images:", error);
        } finally {
            setLoadingImages(false);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleCreateFolder = async () => {
        if (!newFolderName) {
            message.error("Anna kansion nimi");
            return;
        }

        try {
            await api.createFolder(newFolderName, folderId);
            message.success("Kansio luotiin onnistuneesti");
            fetchFolder(folderId, currentPage, pageSize);
            setFolderModalOpen(false);
            setNewFolderName("");
        } catch (error) {
            message.error("Virhe kansion luonnissa");
        }
    };

    const uploadProps = {
        name: "file",
        customRequest: async ({ file, onSuccess, onError }) => {
            const params = {
                folderId: folderId,
            };

            try {
                const response = await api.uploadFile(file, params);
                onSuccess(response);
            } catch (error) {
                onError(error);
                message.error(`${file.name} kuvan siirtäminen epäonnistui.`);
            }
        },
        onChange(info) {
            if (info.file.status !== "uploading") {
                //console.log(info.file, info.fileList);
            }
            if (info.file.status === "done") {
                fetchFolder(folderId, currentPage, pageSize);
                setUploadModalOpen(false);
            } else if (info.file.status === "error") {
                //console.log(`${info.file.name} file upload failed.`);
            }
        },
    };

    const handleDelete = async (id) => {
        try {
            await api.deleteImage(id);
            message.success("Kuva poistettu");
            fetchFolder(folderId, currentPage, pageSize);
        } catch (error) {
            message.error("Kuvan poistaminen epäonnistui");
        }
    };

    const handleFolderDelete = async (id) => {
        try {
            const response = await api.deleteFolder(id);
            message.success("Kansio poistettu");
            navigate(`/oma-sivu/kuvapankki/${response.parentId}`);
        } catch (error) {
            message.error("Kansion poistaminen epäonnistui");
        }
    };

    const handleFolderClick = (folderId) => {
        setCurrentPage(1);
        navigate(`/oma-sivu/kuvapankki/${folderId}`);
    };

    return (
        <>
            <h1 className="text-2xl mb-5">Kuvapankki</h1>

            {loadingImages ? (
                <Spin />
            ) : (
                <>
                    {(isUsersFolder || hasRole(user.roles, "ROLE_SUPERADMIN")) && (
                        <>
                            <Button
                                className="float-right"
                                type="primary"
                                icon={<UploadOutlined />}
                                onClick={() => setUploadModalOpen(true)}
                            >
                                Siirrä kuvia
                            </Button>
                            <Button
                                className="float-right mr-5"
                                type="primary"
                                icon={<FolderAddFilled />}
                                onClick={() => setFolderModalOpen(true)}
                            >
                                Luo kansio
                            </Button>{" "}
                        </>
                    )}
                    <h2>
                        {breadcrumbs.length ? (
                            breadcrumbs.map((folder) => (
                                <span key={folder.id} className="text-gray-500">
                                    {" "}
                                    / {folder.name}
                                </span>
                            ))
                        ) : (
                            <>/</>
                        )}
                    </h2>
                    <Pagination
                        className="mb-5"
                        current={currentPage}
                        pageSize={pageSize}
                        total={totalImages}
                        onChange={handlePageChange}
                    />
                    <div className="grid grid-cols-6 gap-4">
                        {folderId === "0" && folders.length === 0 && images.length === 0 && (
                            <div>Ei kuvia tai kansioita</div>
                        )}

                        {folderId !== "0" && currentPage === 1 && (
                            <div
                                className="flex flex-col items-center justify-center border p-5 cursor-pointer"
                                style={{ width: "150px", height: "150px" }}
                                onClick={() => handleFolderClick(folder.parentId)}
                            >
                                <ArrowLeftOutlined style={{ fontSize: "20px" }} />
                                Ylös
                            </div>
                        )}

                        {folders.map((folder) => (
                            <div
                                key={folder.id}
                                className="flex flex-col items-center justify-center border p-5 cursor-pointer"
                                style={{ width: "150px", height: "150px" }}
                                onClick={() => handleFolderClick(folder.id)}
                            >
                                <FolderFilled className="text-gray-500" style={{ fontSize: "20px" }} />
                                {folder.name}
                            </div>
                        ))}

                        {images?.map((image) => (
                            <div key={image.id} className="relative">
                                {isUsersFolder && (
                                    <Popconfirm
                                        title="Vahvista kuvan poisto"
                                        description=""
                                        okText="Poista"
                                        cancelText="Peruuta"
                                        onConfirm={() => handleDelete(image.id)}
                                    >
                                        <Button
                                            type="primary"
                                            danger
                                            icon={<DeleteFilled />}
                                            className="absolute z-10"
                                            style={{ top: 5, right: 5 }}
                                        />
                                    </Popconfirm>
                                )}
                                <Image
                                    src={image.urls.thumbnail}
                                    preview={{ src: image.urls.original }}
                                    alt={image.name}
                                    className="w-full h-40 object-cover rounded mb-5"
                                    width={165}
                                    height={165}
                                    fallback="data:image/png;base64,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"
                                />
                            </div>
                        ))}
                    </div>
                    <Pagination
                        className="mt-5"
                        current={currentPage}
                        pageSize={pageSize}
                        total={totalImages}
                        onChange={handlePageChange}
                    />

                    {folderId !== "0" && (isUsersFolder || hasRole(user.roles, "ROLE_SUPERADMIN")) && (
                        <div className="flex justify-end mt-5">
                            <Popconfirm
                                title="Vahvista kansion poisto"
                                description="HUOM! Myös kaikki kuvat poistetaan"
                                okText="Poista"
                                cancelText="Peruuta"
                                onConfirm={() => handleFolderDelete(folderId)}
                            >
                                <Button type="primary" danger icon={<DeleteFilled />} className="">
                                    Poista kansio ja kuvat
                                </Button>
                            </Popconfirm>
                        </div>
                    )}
                </>
            )}
            <Modal
                title="Lataa kuvia kuvapankkiin"
                open={uploadModalOpen}
                onCancel={() => setUploadModalOpen(false)}
                footer={null}
            >
                <Dragger {...uploadProps} multiple={true} accept="image/*" showUploadList={false}>
                    <p className="ant-upload-drag-icon">
                        <UploadOutlined />
                    </p>
                    <p className="ant-upload-text">Voit siirtää yhden tai useampia kuvia</p>
                </Dragger>
            </Modal>
            <Modal
                title="Luo uusi kansio"
                open={folderModalOpen}
                onCancel={() => setFolderModalOpen(false)}
                onOk={handleCreateFolder}
                keyboard={true}
            >
                <Input
                    placeholder="Kansion nimi"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onPressEnter={handleCreateFolder}
                />
            </Modal>
        </>
    );
};

export default Gallery;
